<template>
  <div class="request-detail-root">
  <div v-if="request">
    <div class="header-card mb-4">
      <div>
        <p class="eyebrow">Solicitação #{{ request.id }}</p>
        <h3 class="mb-1">{{ humanAction }} · {{ tenantLabel(request.target_id) }}</h3>
        <div class="meta-line">
          <span><strong>Status:</strong> <vs-chip :color="statusColor" size="small">{{ statusLabel }}</vs-chip></span>
          <span><strong>Solicitante:</strong> {{ request.requester ? request.requester.name : '-' }}</span>
          <span><strong>Criado em:</strong> {{ formatDate(request.created_at).full }}</span>
          <span v-if="request.scheduled_at"><strong>Agendado:</strong> {{ formatDate(request.scheduled_at).full }}</span>
          <span v-else><strong>Agendado:</strong> Sem agendamento</span>
          <span>
            <strong>ClickUp:</strong>
            <a v-if="request.clickup_link" :href="request.clickup_link" target="_blank" rel="noopener noreferrer">Abrir</a>
            <span v-else>Não informado</span>
          </span>
        </div>
      </div>
      <div class="actions">
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-arrow-left" @click="$router.back()">Voltar</vs-button>
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-rotate-ccw" @click="reload">Atualizar</vs-button>
      </div>
    </div>

    <vs-card class="mb-4">
      <div class="vx-row">
        <div class="vx-col w-full mb-3">
          <p class="text-xs text-grey">Motivo</p>
          <div class="reason-box">
            {{ request.payload && request.payload.motivo ? request.payload.motivo : '-' }}
          </div>
        </div>
      </div>
      <div class="flex flex-wrap gap-2 mt-2" v-if="canDecide">
        <vs-button color="success" size="small" @click="openDecision('approve')">Aprovar</vs-button>
        <vs-button color="danger" size="small" @click="openDecision('reject')">Recusar</vs-button>
      </div>
    </vs-card>

    <div class="vx-row mt-2">
      <div class="vx-col lg:w-1/2 w-full">
        <vs-card title="Registro da Solicitação">
          <div v-for="step in requestSteps" :key="step.id" class="step-card">
            <div class="flex items-center justify-between">
              <div>
                <p class="step-role">{{ stepTitle(step) }}</p>
                <p class="step-meta">Responsável: {{ stepActor(step) }}</p>
              </div>
              <vs-chip :color="stepChipColor(step)">{{ stepDisplayStatus(step) }}</vs-chip>
            </div>
            <div class="step-footer">
              <span>Revisado em: {{ formatDate(step.reviewed_at).full }}</span>
              <span v-if="step.comment">Comentário: {{ step.comment }}</span>
            </div>
          </div>
        </vs-card>
      </div>
      <div class="vx-col lg:w-1/2 w-full">
        <vs-card title="Timeline">
          <ul class="timeline">
            <li v-for="(entry, index) in timelineItems" :key="`timeline-${index}`">{{ entry }}</li>
          </ul>
        </vs-card>
      </div>
    </div>
  </div>
  <div v-else>
    <vs-alert color="warning" title="Solicitação não encontrada">
      Não foi possível localizar a solicitação.
    </vs-alert>
  </div>

  <vs-popup title="Comentário" :active.sync="decisionModal.active" class="comment-popup">
    <p class="text-sm text-grey mb-2">Adicione um comentário (opcional) para esta decisão.</p>
    <vs-textarea v-model="decisionModal.comment" placeholder="Digite um comentário" class="mb-4" />
    <div class="flex justify-end gap-2">
      <vs-button type="border" @click="closeDecisionModal" :disabled="decisionModal.loading">Voltar</vs-button>
      <vs-button :color="decisionModal.decision === 'approve' ? 'success' : 'danger'" @click="submitDecision" :loading="decisionModal.loading">
        Confirmar
      </vs-button>
    </div>
  </vs-popup>

  </div>
</template>

<script>
import { formatDate, hasPermissionBackOffice, tenantLabel as tenantLabelUtil, filterTenantsByHost, formatTenantLabel, approvalStatusLabel, approvalStatusColor } from '@/utils'
import tenantsRequest from '@/http/requests/tenants'

export default {
  name: 'RequestDetail',
  data() {
    return {
      request: null,
      tenantsMap: {},
      tenantsLoading: false,
      decisionModal: {
        active: false,
        decision: null,
        comment: '',
        loading: false
      }
    }
  },
  watch: {
    '$route.params.id': {
      handler() {
        this.resolveRequest();
      }
    }
  },
  computed: {
    humanAction() {
      if (!this.request) return '-';
      const map = { tenant_deactivation: 'Desativação de tenant' };
      return map[this.request.action_type] || this.request.action_type;
    },
    statusLabel() {
      if (!this.request) return '';
      return approvalStatusLabel(this.request.status);
    },
    statusColor() {
      if (!this.request) return 'primary';
      return approvalStatusColor(this.request.status);
    },
    requestSteps() {
      return this.request && this.request.steps ? this.request.steps : [];
    },
    timelineItems() {
      if (!this.request) {
        return [];
      }

      const items = [`Criado em ${formatDate(this.request.created_at).full}`];

      if (this.request.scheduled_at) {
        items.push(`Agendado para ${formatDate(this.request.scheduled_at).full}`);
      }

      this.requestSteps.forEach((step) => {
        items.push(this.timelineStepLabel(step));
      });

      if (this.request.status === 'canceled' && !this.requestSteps.some(step => this.isScheduleCancellationStep(step))) {
        items.push('Cancelado');
      }

      if (this.request && this.request.scheduled_action && this.request.scheduled_action.result && this.request.scheduled_action.result.success) {
        items.push(`Processamento executado em ${formatDate(this.request.scheduled_action.executed_at).full}`);
      }

      if (this.request && this.request.scheduled_action && this.request.scheduled_action.result && !this.request.scheduled_action.result.success) {
        items.push(`Processo falhou com '${this.formatErrorMessage(this.request.scheduled_action.result.message)}' em ${formatDate(this.request.scheduled_action.executed_at).full}`);
      }

      return items;
    },
    canDecide() {
      if (!this.request) return false;
      if (this.isOwner) return false;
      const pendingStep = this.getPendingStep(this.request);
      return hasPermissionBackOffice('approval-update') && this.request.status === 'pending' && pendingStep && (!pendingStep.approver_id || pendingStep.approver_id === this.currentUserId);
    },
    currentUserId() {
      return this.$store.state.AppActiveUser ? this.$store.state.AppActiveUser.id : null;
    },
    isOwner() {
      if (!this.request) return false;
      const requesterId = this.request.requester ? this.request.requester.id : null;
      return requesterId === this.currentUserId || this.request.requested_by === this.currentUserId;
    }
  },
  methods: {
    formatDate,
    approvalStatusLabel,
    approvalStatusColor,
    openDecision(decision) {
      if (!this.canDecide) return;
      if (decision === 'approve') {
        this.submitDecisionDirect(decision, null);
        return;
      }
      this.decisionModal = { active: true, decision, comment: '', loading: false };
    },
    closeDecisionModal() {
      this.decisionModal.comment = '';
      this.decisionModal.active = false;
      this.decisionModal.loading = false;
      this.decisionModal.decision = null;
    },
    async submitDecision() {
      const decision = this.decisionModal.decision;
      const comment = this.decisionModal.comment || null;
      await this.submitDecisionDirect(decision, comment, true);
      this.closeDecisionModal();
    },
    async submitDecisionDirect(decision, comment = null, fromModal = false) {
      if (!this.request) return;
      this.decisionModal.loading = fromModal;
      await this.$store.dispatch('approvals/decideRequest', {
        id: this.request.id,
        decision,
        comment,
        roleRequired: this.getPendingStep(this.request) ? this.getPendingStep(this.request).role_required : null
      }).then((data) => {
        this.request = data;
        this.$vs.notify({ color: decision === 'approve' ? 'success' : 'danger', title: 'Solicitação', text: `Solicitação ${decision === 'approve' ? 'aprovada' : 'recusada'}.` });
      }).catch((error) => {
        const status = error.response && error.response.status;
        const message = status === 403 ? 'Você não tem permissão para aprovar/recusar.' : 'Não foi possível enviar a decisão.';
        this.$vs.notify({ color: 'danger', title: 'Erro', text: message });
      }).finally(() => {
        this.decisionModal.loading = false;
      });
    },
    tenantLabel(id) {
      return tenantLabelUtil(this.tenantsMap, id);
    },
    formatErrorMessage(message) {
      if (!message) return '';
      try {
        const parsed = typeof message === 'string' ? JSON.parse(message) : message;
        if (parsed && parsed.message) return parsed.message;
        if (typeof parsed === 'object') return JSON.stringify(parsed);
      } catch (e) {
        return message;
      }
      return message;
    },
    getPendingStep(item) {
      if (!item.steps) return null;
      return item.steps.find(step => step.status === 'pending') || null;
    },
    stepActor(step) {
      return step && step.approver && step.approver.name ? step.approver.name : '-';
    },
    isAutomaticApprovalStep(step) {
      return !!(step && step.comment === 'Aprovada automaticamente pelo solicitante.');
    },
    isScheduleUpdateStep(step) {
      return !!(step && step.comment && step.comment.startsWith('Agendamento atualizado.'));
    },
    isScheduleCancellationStep(step) {
      return !!(step && step.comment && step.comment.startsWith('Agendamento cancelado.'));
    },
    stepTitle(step) {
      if (this.isAutomaticApprovalStep(step)) {
        return 'Solicitante';
      }

      if (this.isScheduleUpdateStep(step)) {
        return 'Agendamento atualizado';
      }

      if (this.isScheduleCancellationStep(step)) {
        return 'Agendamento cancelado';
      }

      return this.stepRoleLabel(step.role_required);
    },
    stepDisplayStatus(step) {
      if (this.isScheduleUpdateStep(step)) {
        return 'Atualizado';
      }

      if (this.isScheduleCancellationStep(step)) {
        return 'Cancelado';
      }

      return this.stepStatusLabel(step.status);
    },
    stepChipColor(step) {
      if (this.isScheduleCancellationStep(step) || ['rejected', 'failed', 'canceled'].includes(step.status)) {
        return 'danger';
      }

      if (this.isScheduleUpdateStep(step) || ['executed', 'approved'].includes(step.status)) {
        return 'success';
      }

      return 'warning';
    },
    timelineStepLabel(step) {
      const reviewedAt = formatDate(step.reviewed_at).full;

      if (this.isAutomaticApprovalStep(step)) {
        return `Solicitante aprovado${reviewedAt ? ' em ' + reviewedAt : ''}`;
      }

      if (this.isScheduleUpdateStep(step)) {
        return `Agendamento atualizado por ${this.stepActor(step)}${reviewedAt ? ' em ' + reviewedAt : ''}`;
      }

      if (this.isScheduleCancellationStep(step)) {
        return `Agendamento cancelado por ${this.stepActor(step)}${reviewedAt ? ' em ' + reviewedAt : ''}`;
      }

      return `${this.stepRoleLabel(step.role_required)} ${this.stepStatusLabel(step.status)}${reviewedAt ? ' em ' + reviewedAt : ''}`;
    },
    stepRoleLabel(role) {
      const map = {
        manager: 'Gestor',
        finance: 'Financeiro',
        history: 'Histórico'
      };
      return map[role] || role;
    },
    stepStatusLabel(status) {
      const map = {
        pending: 'Pendente',
        approved: 'Aprovado',
        rejected: 'Recusado',
        canceled: 'Cancelado',
        executed: 'Executado',
        failed: 'Falhou'
      };
      return map[status] || status;
    },
    async loadTenants() {
      try {
        this.tenantsLoading = true;
        const response = await tenantsRequest.fetchPaginatedTenants(
          localStorage.getItem('accessToken'),
          true,
          1,
          500,
          '',
          'fqdn',
          'ASC'
        );
        const data = response.data || {};
        const items = filterTenantsByHost(data.items || data.data || data || []);
        const map = {};
        items.forEach(item => {
          map[item.id] = formatTenantLabel(item);
        });
        this.tenantsMap = map;
      } catch (error) {
        console.error(error);
      } finally {
        this.tenantsLoading = false;
      }
    },
    async reload() {
      await this.resolveRequest();
    },
    async resolveRequest() {
      const id = Number(this.$route.params.id);
      const current = this.$store.state.approvals.currentRequest;
      if (current && current.id === id) {
        this.request = current;
        return;
      }

      const fetched = await this.$store.dispatch('approvals/fetchRequestById', id).catch(() => null);
      this.request = fetched || null;
    }
  },
  async created() {
    await this.loadTenants();
    await this.resolveRequest();
  }
}
</script>

<style scoped>
.timeline {
  list-style: disc;
  padding-left: 18px;
}

.timeline li {
  margin-bottom: 8px;
}

.vs-alert {
  max-width: 600px;
}

.header-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}

.eyebrow {
  text-transform: uppercase;
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 4px;
  letter-spacing: 0.4px;
}

.meta-line {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-top: 6px;
  font-size: 13px;
}

.meta-line span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.reason-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  min-height: 48px;
  word-break: break-word;
}

.step-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  background: #fff;
}

.step-role {
  font-weight: 700;
  margin: 0;
  text-transform: capitalize;
}

.step-meta {
  margin: 2px 0 0 0;
  color: #6b7280;
  font-size: 12px;
}

.step-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
  color: #6b7280;
  font-size: 12px;
}

.comment-popup ::v-deep .vs-popup {
  max-width: 520px;
}
</style>
